import React, {useEffect} from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@linaria/core'
import {Divider} from '@vio/atlas/Divider'
import {getOverlay} from 'modules/overlay/selectors'
import {Overlay} from 'modules/overlay/types'
import {getIsMapOpen} from 'modules/search/selectors'

import {useBrand} from '@daedalus/core/src/_web/brand/hooks/useBrand'

import {
  EndContentWrapper,
  HeaderElement,
  HeaderTopContentElement
} from './styles'

// Since we don't have access to css variables inside the header, we need to set the theme color manually
const NO_COLOR_MODE = '#fff'
const COLORED_MODE = '#170033'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Logo component to be displayed in the top bar */
  brandLogo?: React.ReactNode
  /** Whether to display a bottom shadow */
  hasShadow?: boolean
  /** Custom content (element or text) to render in the center of the header */
  centerContent?: React.ReactNode
  /** Custom content to be displayed on the end with priority */
  endContent?: React.ReactNode
  /** Custom content to be displayed on the bottom with priority */
  bottomContent?: React.ReactNode
  /** Whether the search box is open */
  isSearchBoxOpen?: boolean
  /** Whether to show the divider */
  hasDivider?: boolean
  /** Whether to show the compact header */
  isCompact?: boolean
}

interface ContainerProps {
  brandIsVio: boolean
  isMapOpen: boolean
  overlayState: Overlay[]
  hasCustomHeader?: boolean
}

export const SearchNewTopBar = (props: Props) => {
  const {brandIsVio, brandHasCustomHeader} = useBrand()
  const isMapOpen = useSelector(getIsMapOpen)
  const overlayState = useSelector(getOverlay)

  return (
    <SearchNewTopBarComponent
      {...props}
      brandIsVio={brandIsVio}
      isMapOpen={isMapOpen}
      overlayState={overlayState}
      hasCustomHeader={brandHasCustomHeader}
    />
  )
}
export const SearchNewTopBarComponent = ({
  className,
  brandLogo,
  centerContent,
  endContent,
  hasShadow = false,
  bottomContent = null,
  isSearchBoxOpen = false,
  hasDivider = false,
  isCompact = false,
  brandIsVio,
  isMapOpen,
  overlayState,
  hasCustomHeader = false
}: Props & ContainerProps) => {
  const isOverlayOpen = overlayState.length > 0

  const isWhiteMode =
    isSearchBoxOpen || isMapOpen || isOverlayOpen || !brandIsVio

  useEffect(() => {
    let meta = document.querySelector('meta[name=theme-color]')
    if (!meta) {
      meta = document.createElement('meta')

      meta.setAttribute('name', 'theme-color')

      document.head.appendChild(meta)
    }
    meta.setAttribute('content', isWhiteMode ? NO_COLOR_MODE : COLORED_MODE)
    return () => {
      meta.setAttribute('content', NO_COLOR_MODE)
    }
  }, [isWhiteMode])

  return (
    <>
      <HeaderElement
        className={cx(
          className,
          hasShadow && '__shadow',
          isSearchBoxOpen && '__hideBackground',
          isCompact && '__compact',
          hasCustomHeader && '__hasCustomHeader'
        )}
      >
        {!hasCustomHeader && (
          <HeaderTopContentElement
            className={cx(
              isCompact && '__compact',
              isSearchBoxOpen && '__isSearchBoxOpen'
            )}
          >
            <>{brandLogo}</>
            <EndContentWrapper
              className={isSearchBoxOpen && '__hasBottomPadding'}
            >
              {endContent}
            </EndContentWrapper>
          </HeaderTopContentElement>
        )}
        {!isSearchBoxOpen && <div>{centerContent}</div>}
        <div>{bottomContent}</div>
      </HeaderElement>
      {hasDivider && <Divider />}
    </>
  )
}
